/* eslint-disable no-case-declarations */
import { extractAllNumbers, generateUUID, toBlob } from "@zyzgroup/core-common";
import { DOMToBlob, DOMToString } from "../file";
import { a2c, a2c2, l2c, q2c } from "./line2c";
import { useAttribute, useClickA } from "../hooks";

export type TSVGParsedPathData = [command: string, ...args: number[]];

export const SVGParsedPathArgsLength: Record<string, number> = {
  a: 7, // A rx,ry xAxisRotate LargeArcFlag,SweepFlag x,y
  c: 6, // C c1x,c1y c2x,c2y x,y
  s: 4, // S c2x,c2y x,y
  q: 4, // Q cx,cy x,y
  t: 2, // T x,y
  m: 2, // M x,y
  l: 2, // L x,y
  h: 1, // H x
  v: 1, // V y
  z: 0 // Z
  // r: 4
};

// console.log(
//   DOMSVGUtil.SVGPathDataToD(
//     DOMSVGUtil.relativeSVGPath(
//       DOMSVGUtil.parseSVGPath(
//         "M17.7722 6.22781C18.0759 6.53155 18.0759 7.02401 17.7722 7.32775L7.32775 17.7722C7.02401 18.0759 6.53155 18.0759 6.22781 17.7722C5.92406 17.4685 5.92407 16.976 6.22781 16.6722L16.6722 6.22781C16.976 5.92406 17.4684 5.92406 17.7722 6.22781Z"
//       )
//     )
//   )
// );

export class DOMSVGUtil {
  static createSVGElement(
    tagName = "path",
    attrs?: Record<string, string>
  ): SVGGeometryElement {
    const el = document.createElementNS.call(
      document,
      "http://www.w3.org/2000/svg",
      tagName
    ) as SVGGeometryElement;
    if (attrs) {
      useAttribute(el, attrs);
    }
    return el;
  }

  static createSvgPath(d: string, id?: string): SVGPathElement {
    const path = this.createSVGElement("path", { d, id: id || generateUUID() });
    return path as unknown as SVGPathElement;
  }

  static downloadFromSVGElement(svgContainer: SVGElement, name = "shape") {
    return useClickA(
      DOMToBlob(svgContainer as unknown as HTMLElement),
      `${name}.svg`
    );
  }

  static downloadFromSVGString(svgString: string, name = "shape") {
    return useClickA(toBlob(svgString), `${name}.svg`);
  }

  static getSVGRawStringFromElement(el: HTMLElement) {
    return DOMToString(el);
  }

  // https://github.com/dy/is-svg-path
  // isSVGPath('M0 0L10 20 20 0Z') //true
  // isSVGPath('M00Z') //false
  // isSVGPath('xyz') //false
  static isSVGPath(str: string) {
    if (typeof str !== "string") return false;
    str = str.trim();
    // https://www.w3.org/TR/SVG/paths.html#PathDataBNF
    return (
      /^[mlhvzaqtcs]\s*[-+.0-9][^mlhvzaqtcs]+/i.test(str) && /[\dz]$/i.test(str)
      // && str.length > 4
    );
  }

  static SVGPathDataToD(path: TSVGParsedPathData[]): string {
    return path
      .map((item) => {
        if (!item[0]) {
          return "";
        }
        switch (item[0].toLowerCase()) {
          case "a":
            return `${item[0]} ${item[1]},${item[2]} ${item[3]} ${item[4]},${item[5]} ${item[6]},${item[7]}`;
          case "c":
            return `${item[0]} ${item[1]},${item[2]} ${item[3]},${item[4]} ${item[5]},${item[6]}`;
          case "s":
          case "q":
            return `${item[0]} ${item[1]},${item[2]} ${item[3]},${item[4]}`;
          default:
            return item[0] + " " + item.slice(1).join(",");
        }
      })
      .join(" ");
  }

  /// parse an svg path data string to `[[command, arg1, arg2, ...], ...]`
  static parseSVGPath(path: string): TSVGParsedPathData[] {
    const data: TSVGParsedPathData[] = [];
    if (!DOMSVGUtil.isSVGPath(path)) {
      // throw new Error(`parseSVGPath(path = ${path}) is not a SVG path!`);
      return data;
    }
    path.replace(
      /([mlhvzaqtcs])([^mlhvzaqtcs]*)/gi,
      (substring: string, command: string, argsString: string): string => {
        let type = command.toLowerCase();
        const args: number[] = extractAllNumbers(argsString);

        // overloaded moveTo
        if (type == "m" && args.length > SVGParsedPathArgsLength[type]) {
          data.push([
            command,
            ...args.splice(0, SVGParsedPathArgsLength[type])
          ]);
          type = "l";
          command = command == "m" ? "l" : "L";
        }

        while (args.length) {
          if (args.length < SVGParsedPathArgsLength[type]) {
            throw new Error(
              `parseSVGPath substring:{ ${substring} } command:{ ${command} } args:{ ${args} }`
            );
          } else {
            data.push([
              command,
              ...args.splice(0, SVGParsedPathArgsLength[type])
            ]);
          }
        }

        if (type == "z") {
          data.push([command]);
        }

        return "";
      }
    );
    return data;
  }

  /// https://github.com/jkroso/abs-svg-path
  static absolutizeSVGPath(path: TSVGParsedPathData[]): TSVGParsedPathData[] {
    let startX = 0;
    let startY = 0;
    let x = 0;
    let y = 0;

    return path.map(function (seg) {
      const type = seg[0];
      const command = type.toUpperCase();

      // is relative
      if (type != command) {
        seg[0] = command;
        switch (type) {
          case "a":
            seg[6] += x;
            seg[7] += y;
            break;
          case "v":
            seg[1] += y;
            break;
          case "h":
            seg[1] += x;
            break;
          default:
            for (let i = 1; i < seg.length; ) {
              (seg[i++] as number) += x;
              (seg[i++] as number) += y;
            }
        }
      }

      switch (command) {
        case "Z":
          x = startX;
          y = startY;
          break;
        case "H":
          x = seg[1];
          break;
        case "V":
          y = seg[1];
          break;
        case "M":
          x = startX = seg[1];
          y = startY = seg[2];
          break;
        default:
          x = seg[seg.length - 2] as number;
          y = seg[seg.length - 1] as number;
      }

      return seg;
    });
  }

  static relativeSVGPath(path: TSVGParsedPathData[]): TSVGParsedPathData[] {
    let startX = 0;
    let startY = 0;
    let x = 0;
    let y = 0;

    return path.map(function (seg) {
      const type = seg[0];
      const command = type.toLowerCase();

      // is absolute
      if (type != command) {
        seg[0] = command;
        switch (type) {
          case "A":
            seg[6] -= x;
            seg[7] -= y;
            break;
          case "V":
            seg[1] -= y;
            break;
          case "H":
            seg[1] -= x;
            break;
          default:
            for (let i = 1; i < seg.length; ) {
              (seg[i++] as number) -= x;
              (seg[i++] as number) -= y;
            }
        }
      }

      switch (command) {
        case "z":
          x = startX;
          y = startY;
          break;
        case "h":
          x += seg[1];
          break;
        case "v":
          y += seg[1];
          break;
        case "m":
          x += seg[1];
          y += seg[2];
          startX += seg[1];
          startY += seg[2];
          break;
        default:
          x += seg[seg.length - 2] as number;
          y += seg[seg.length - 1] as number;
      }

      return seg;
    });
  }

  /// 统统变为 CubicBezier
  /// https://github.com/jkroso/normalize-svg-path
  static normalizeSVGPath(path: TSVGParsedPathData[]): TSVGParsedPathData[] {
    const result: TSVGParsedPathData[] = [];
    let startX = 0;
    let startY = 0;
    let x = 0;
    let y = 0;
    let quadX = 0;
    let quadY = 0;
    let bezierX = 0;
    let bezierY = 0;

    let prevCommand;

    for (let i = 0, len = path.length; i < len; i++) {
      let seg = path[i];
      const command = seg[0];

      switch (command) {
        case "M":
          startX = seg[1];
          startY = seg[2];
          break;
        case "A":
          const curves = a2c(
            x,
            y,
            seg[6],
            seg[7],
            seg[4],
            seg[5],
            seg[1],
            seg[2],
            seg[3]
          );
          // const curves = a2c2(
          //   x,
          //   y,
          //   seg[1],
          //   seg[2],
          //   seg[3],
          //   seg[4],
          //   seg[5],
          //   seg[6],
          //   seg[7]
          // );
          if (!curves.length) continue;
          curves.forEach((curve, j) => {
            /// a2c
            const [x0, y0, x1, y1, x2, y2, x3, y3] = curve;
            /// a2c2
            // const [x1, y1, x2, y2, x3, y3] = curve;
            seg = ["C", x1, y1, x2, y2, x3, y3];
            if (j < curves.length - 1) {
              result.push(seg);
            }
          });
          break;
        case "S":
          // default control point
          let cx = x;
          let cy = y;
          if (prevCommand == "C" || prevCommand == "S") {
            cx += cx - bezierX; // reflect the previous command's control
            cy += cy - bezierY; // point relative to the current point
          }
          seg = ["C", cx, cy, seg[1], seg[2], seg[3], seg[4]];
          break;
        case "T":
          if (prevCommand == "Q" || prevCommand == "T") {
            quadX = x * 2 - quadX; // as with 'S' reflect previous control point
            quadY = y * 2 - quadY;
          } else {
            quadX = x;
            quadY = y;
          }
          seg = ["C", ...q2c(x, y, quadX, quadY, seg[1], seg[2])];
          break;
        case "Q":
          quadX = seg[1];
          quadY = seg[2];
          seg = ["C", ...q2c(x, y, seg[1], seg[2], seg[3], seg[4])];
          break;
        case "L":
          seg = ["C", ...l2c(x, y, seg[1], seg[2])];
          break;
        case "H":
          seg = ["C", ...l2c(x, y, seg[1], y)];
          break;
        case "V":
          seg = ["C", ...l2c(x, y, x, seg[1])];
          break;
        case "Z":
          seg = ["C", ...l2c(x, y, startX, startY)];
          break;
      }

      prevCommand = command;
      x = seg[seg.length - 2] as number;
      y = seg[seg.length - 1] as number;
      if (seg.length > 4) {
        bezierX = seg[seg.length - 4] as number;
        bezierY = seg[seg.length - 3] as number;
      } else {
        bezierX = x;
        bezierY = y;
      }

      result.push(seg);
    }

    return result;
  }

  /// http://schepers.cc/getting-to-the-point
  /// Catmull-Rom curves : `R x1,y1 x2,y2 x3,y3`
  static catmullRom2bezier(crp: number[]): TSVGParsedPathData[] {
    const d: TSVGParsedPathData[] = [];
    for (let i = 0, iLen = crp.length; iLen - 2 > i; i += 2) {
      let p;
      if (0 == i) {
        p = [
          { x: crp[i], y: crp[i + 1] },
          { x: crp[i], y: crp[i + 1] },
          { x: crp[i + 2], y: crp[i + 3] },
          { x: crp[i + 4], y: crp[i + 5] }
        ];
      } else if (iLen - 4 == i) {
        p = [
          { x: crp[i - 2], y: crp[i - 1] },
          { x: crp[i], y: crp[i + 1] },
          { x: crp[i + 2], y: crp[i + 3] },
          { x: crp[i + 2], y: crp[i + 3] }
        ];
      } else {
        p = [
          { x: crp[i - 2], y: crp[i - 1] },
          { x: crp[i], y: crp[i + 1] },
          { x: crp[i + 2], y: crp[i + 3] },
          { x: crp[i + 4], y: crp[i + 5] }
        ];
      }

      // Catmull-Rom to Cubic Bezier conversion matrix
      //    0       1       0       0
      //  -1/6      1      1/6      0
      //    0      1/6      1     -1/6
      //    0       0       1       0

      const bp = [
        { x: p[1].x, y: p[1].y },
        {
          x: (-p[0].x + 6 * p[1].x + p[2].x) / 6,
          y: (-p[0].y + 6 * p[1].y + p[2].y) / 6
        },
        {
          x: (p[1].x + 6 * p[2].x - p[3].x) / 6,
          y: (p[1].y + 6 * p[2].y - p[3].y) / 6
        },
        { x: p[2].x, y: p[2].y }
      ];

      d.push(["C", bp[1].x, bp[1].y, bp[2].x, bp[2].y, bp[3].x, bp[3].y]);
    }

    return d;
  }

  // Convert 'Z', 'V' and 'H' segments to 'L' segments
  static zvhToLSVGPath(path: TSVGParsedPathData[]): TSVGParsedPathData[] {
    const result: TSVGParsedPathData[] = [];
    let startPoint: TSVGParsedPathData = ["L", 0, 0];
    let lastPoint: TSVGParsedPathData;
    for (let i = 0, len = path.length; i < len; i++) {
      const pt = path[i];
      switch (pt[0]) {
        case "M":
          startPoint = ["L", pt[1], pt[2]];
          result.push(pt);
          break;
        case "Z":
          result.push(startPoint);
          break;
        case "H":
          lastPoint = result[result.length - 1] || ["L", 0, 0];
          result.push([
            "L",
            pt[1],
            lastPoint[lastPoint.length - 1]
          ] as TSVGParsedPathData);
          break;
        case "V":
          lastPoint = result[result.length - 1] || ["L", 0, 0];
          result.push([
            "L",
            lastPoint[lastPoint.length - 2],
            pt[1]
          ] as TSVGParsedPathData);
          break;
        default:
          result.push(pt);
      }
    }
    return result;
  }

  // Expand shorthand curve commands to full versions; mutates the path in place for efficiency
  // Requires commands have already been converted to absolute versions
  static longhandSVGPath(path: TSVGParsedPathData[]): TSVGParsedPathData[] {
    const conversion: Record<string, { to: string; x: number }> = {
      S: { to: "C", x: 3 },
      T: { to: "Q", x: 1 }
    };
    let prev;
    let x1 = 0;
    let y1 = 0;
    for (let i = 0, len = path.length; i < len; i++) {
      const cmd = path[i];
      const convert = conversion[cmd[0]];
      if (convert) {
        cmd[0] = convert.to;
        if (prev) {
          if (prev[0] === convert.to) {
            x1 = 2 * Number(prev[convert.x + 2]) - Number(prev[convert.x]);
            y1 = 2 * Number(prev[convert.x + 3]) - Number(prev[convert.x + 1]);
          } else {
            x1 = Number(prev[prev.length - 2]);
            y1 = Number(prev[prev.length - 1]);
          }
        }
        cmd.splice(1, 0, x1, y1);
      }
      prev = cmd;
    }
    return path;
  }
}

export default DOMSVGUtil;
